import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetHotelByIdQuery, useUpdateHotelMutation } from '../Service/hotelsApi';
import { HotelsRoutes } from '../../../Router/path';
import { CircularProgress, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import HotelIcon from '@mui/icons-material/Hotel';
import { hotelFields } from '../fields';
import { Hotel } from '../Types/hotelTypes';
import ImageIcon from '@mui/icons-material/Image';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { hotelValues } from '../initialValus';
import { useGetHotelWithImagesQuery } from '../Service/extendedHotelsApi';
import UploadedImagesGallery from '../Components/UploadedImagesGallery';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import UploadImagesForm from '../Components/UploadImagesForm';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import theme from '../../../theme';


const EditHotel = () => {
    const hotelId = parseInt(useParams<{ hotelId: string }>().hotelId!);

    const { data: hotelData, isLoading: isHotelLoading, error: hotelError } = useGetHotelByIdQuery(hotelId, {
        skip: !hotelId,
    });

    const {
        data: hotelWithImagesData,
        isLoading: isImagesLoading,
        isError: isImagesError,
        refetch: refetchImages,
    } = useGetHotelWithImagesQuery(hotelId);

    const [updateHotel, updateStatus] = useUpdateHotelMutation();

    const entityForm = useEntityForm<Hotel>({
        initialData: hotelData ?? hotelValues,
        fields: hotelFields,
        submitFn: (data) => updateHotel({ id: hotelId, data }).unwrap(),
        redirectUrl: HotelsRoutes.root,
        title: 'Edytuj hotel',
        icon: <HotelIcon />,
        backUrl: HotelsRoutes.root,
        groupOrder: [FormRenderGroupTypes.basic, FormRenderGroupTypes.address, FormRenderGroupTypes.seo, FormRenderGroupTypes.configuration],
        groupIcons: {
            Podstawowe: <InfoIcon sx={{ color: theme.palette.primary.main }} />,
            SEO: <SearchIcon sx={{ color: theme.palette.primary.main }} />,
            Konfiguracja: <SettingsIcon sx={{ color: theme.palette.primary.main }} />,
            Adres: <LocationOnIcon sx={{ color: theme.palette.primary.main }} />
        },
        mutationStatus: updateStatus
    });

    if (isHotelLoading) {
        return (
            <Typography align="center" sx={{ mt: 4 }}>
                <CircularProgress />
            </Typography>
        );
    }

    if (hotelError) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                Nie udało się załadować danych hotelu.
            </Typography>
        );
    }

    return (
      <>
        <EntityFormLayout {...entityForm}>
          <AccordionCard title="Wgrane zdjęcia" icon={<ImageIcon />}>
            {hotelWithImagesData?.images?.length ? (
              <>
                <UploadedImagesGallery
                  images={hotelWithImagesData.images}
                  isLoading={isImagesLoading}
                  isError={isImagesError}
                  hotelId={hotelId}
                  onDeleteSuccess={refetchImages}
                />
                <UploadImagesForm hotelId={hotelId} onUploadSuccess={refetchImages} />
              </>
            ) : (
              <Typography color="text.secondary" component="div">
                Nie wgrano jeszcze żadnych zdjęć.
                <UploadImagesForm hotelId={hotelId} onUploadSuccess={refetchImages} />
              </Typography>
            )}
          </AccordionCard>
        </EntityFormLayout>
      </>
    );
};

export default EditHotel;
